import { Pencil } from 'lucide-react';
import { useState } from 'react';

import { useEnvironmentId } from '@/react/hooks/useEnvironmentId';
import { useEnvironment } from '@/react/portainer/environments/queries';
import { useIsPureAdmin } from '@/react/hooks/useUser';
import { usePolicy } from '@/react/portainer/policies/queries/usePolicy';
import { getK8sAppliedPolicies } from '@/react/portainer/policies/utils/policy-types-metadata';
import { PolicyType } from '@/react/portainer/policies/types';
import { useKubernetesSecurityInitialValues } from '@/react/portainer/policies/components/kubernetes/security/useKubernetesSecurityInitialValues';
import { useKubernetesSetupInitialValues } from '@/react/portainer/policies/components/kubernetes/setup/useKubernetesSetupInitialValues';
import { useKubernetesRBACInitialValues } from '@/react/portainer/policies/components/kubernetes/rbac/useKubernetesRBACInitialValues';
import { useKubernetesRegistryInitialValues } from '@/react/portainer/policies/components/kubernetes/registry/useKubernetesRegistryInitialValues';
import { AllowedSourcesReadOnly } from '@/react/portainer/policies/components/kubernetes/registry/AllowedSourcesReadOnly';
import { getStatusDisplayLabel } from '@/react/portainer/policies/DetailView/AttachmentsDataTables/statusUtils';
import {
  AppliedPolicyStatus,
  AppliedPolicyStatusType,
} from '@/react/portainer/environments/types';

import { PageHeader } from '@@/PageHeader';
import { Widget, WidgetBody } from '@@/Widget';
import { Badge, BadgeType } from '@@/Badge';
import { Link } from '@@/Link';
import { LinkButton } from '@@/LinkButton';
import { CollapseExpandButton } from '@@/CollapseExpandButton';
import { TextTip } from '@@/Tip/TextTip';

import { SecurityPolicyReadOnly } from './components/SecurityPolicyReadOnly';
import { SetupPolicyReadOnly } from './components/SetupPolicyReadOnly';
import { RBACPolicyReadOnly } from './components/RBACPolicyReadOnly';
import { RegistryPolicyReadOnly } from './components/RegistryPolicyReadOnly';

export function PoliciesView() {
  const environmentId = useEnvironmentId();
  const environmentQuery = useEnvironment(environmentId);
  const isPureAdmin = useIsPureAdmin();

  const policies = environmentQuery.data?.Policies;

  const appliedPolicies = getK8sAppliedPolicies(policies);
  const columns =
    appliedPolicies.length === 1
      ? [appliedPolicies]
      : splitAppliedPolicies(appliedPolicies);
  const containerClassName =
    columns.length === 1
      ? 'flex flex-col gap-4'
      : 'flex flex-col lg:flex-row gap-4';
  const columnClassName =
    columns.length === 1 ? 'flex flex-col gap-4' : 'flex flex-1 flex-col gap-4';
  const showEmptyState =
    !environmentQuery.isError &&
    (environmentQuery.isLoading || appliedPolicies.length === 0);

  return (
    <>
      <PageHeader
        title="Policies"
        breadcrumbs={[{ label: 'Fleet Governance Policies' }]}
        reload
      />

      <div className="row">
        <div className="col-sm-12">
          {environmentQuery.isError && (
            <TextTip color="red" className="mb-4">
              Failed to load policies for this environment. Please try again.
            </TextTip>
          )}

          {showEmptyState && (
            <Widget>
              <WidgetBody loading={environmentQuery.isLoading}>
                {!environmentQuery.isLoading && (
                  <div className="text-muted">
                    <p className="mb-2">
                      No policies are currently applied to this environment.
                    </p>
                    <p className="mb-2">
                      Policies allow you to enforce security settings, configure
                      cluster resources, manage RBAC, and control registry
                      access across multiple environments.
                    </p>
                    {isPureAdmin ? (
                      <p>
                        To create and manage policies, visit the{' '}
                        <Link
                          to="portainer.policies"
                          data-cy="policies-management-link"
                        >
                          Fleet Governance Policies
                        </Link>{' '}
                        page.
                      </p>
                    ) : (
                      <p>
                        Contact your Portainer administrator to enable policies
                        for this environment.
                      </p>
                    )}
                  </div>
                )}
              </WidgetBody>
            </Widget>
          )}

          {!environmentQuery.isError && appliedPolicies.length > 0 && (
            // Keep separate columns so each can collapse independently without grid row coupling.
            <div className={containerClassName}>
              {columns.map((column, columnIndex) => (
                <div key={columnIndex} className={columnClassName}>
                  {column.map((item) => renderPolicySection(item, isPureAdmin))}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function splitAppliedPolicies(
  appliedPolicies: ReturnType<typeof getK8sAppliedPolicies>
) {
  type AppliedPolicyItem = (typeof appliedPolicies)[number];

  // Alternate items between left/right columns while preserving per-column order.
  return appliedPolicies.reduce<[AppliedPolicyItem[], AppliedPolicyItem[]]>(
    (result, item, index) => {
      result[index % 2].push(item);
      return result;
    },
    [[], []]
  );
}

function renderPolicySection(
  item: ReturnType<typeof getK8sAppliedPolicies>[number],
  isPureAdmin: boolean
) {
  const policyId = item.policy?.policyID;
  if (!policyId) {
    return null;
  }

  return (
    <PolicySection
      key={item.key}
      label={item.label}
      policyId={policyId}
      policyName={item.policy?.policyName}
      policyType={item.type}
      status={item.policy?.policyStatus}
      canEdit={isPureAdmin}
      canExpand
    />
  );
}

function getStatusBadgeType(status: AppliedPolicyStatusType): BadgeType {
  if (status === AppliedPolicyStatusType.Applied) return 'success';
  if (status === AppliedPolicyStatusType.Failed) return 'danger';
  if (status === AppliedPolicyStatusType.Warning) return 'warn';
  if (status === AppliedPolicyStatusType.InProgress) return 'info';
  if (status === AppliedPolicyStatusType.NotSupported) return 'muted';
  return 'muted';
}

function getStatusTipColor(
  status: AppliedPolicyStatusType
): 'red' | 'orange' | 'blue' | undefined {
  if (status === AppliedPolicyStatusType.Failed) return 'red';
  if (status === AppliedPolicyStatusType.Warning) return 'orange';
  if (status === AppliedPolicyStatusType.InProgress) return 'blue';
  return undefined;
}

function getStatusLabel(status: AppliedPolicyStatusType) {
  switch (status) {
    case AppliedPolicyStatusType.Applied:
    case AppliedPolicyStatusType.Failed:
    case AppliedPolicyStatusType.Warning:
    case AppliedPolicyStatusType.InProgress:
    case AppliedPolicyStatusType.NotSupported:
      return getStatusDisplayLabel(status);
    default:
      return status;
  }
}

interface PolicySectionProps {
  label: string;
  policyId: number;
  policyName?: string;
  policyType: PolicyType;
  status?: AppliedPolicyStatus;
  canEdit: boolean;
  canExpand: boolean;
}

function PolicySection({
  label,
  policyId,
  policyName,
  policyType,
  status,
  canEdit,
  canExpand,
}: PolicySectionProps) {
  const [isExpanded, setIsExpanded] = useState(canExpand);
  const policyQuery = usePolicy(policyId, { enabled: canExpand });
  const policy = policyQuery.data;
  const contentId = `policy-section-${policyId}`;

  const headerContent = (
    <div className="widget-title flex w-full items-center gap-2">
      {canExpand && (
        <CollapseExpandButton
          isExpanded={isExpanded}
          onClick={() => setIsExpanded((value) => !value)}
          data-cy={`collapse-policy-${policyId}`}
        />
      )}
      <button
        type="button"
        className="flex-1 flex items-center gap-2 text-left border-0 bg-transparent p-0 focus:outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-5 focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50"
        onClick={() => canExpand && setIsExpanded((value) => !value)}
        disabled={!canExpand}
        aria-expanded={isExpanded}
        aria-controls={contentId}
        aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${label} ${
          policyName || ''
        } policy section`}
      >
        <span className="flex-1">
          {label} - {policyName || 'Unknown'}
        </span>
        {status?.Status && (
          <Badge type={getStatusBadgeType(status.Status)}>
            {getStatusLabel(status.Status)}
          </Badge>
        )}
      </button>
      {canEdit && (
        <LinkButton
          to="portainer.policy"
          params={{ id: policyId }}
          icon={Pencil}
          size="small"
          color="default"
          data-cy={`edit-policy-button-${policyId}`}
        >
          Edit
        </LinkButton>
      )}
    </div>
  );

  return (
    <Widget>
      <div className="widget-header w-full">
        <div className="row">
          <div className="col-sm-12">{headerContent}</div>
        </div>
      </div>
      <WidgetBody
        loading={policyQuery.isLoading}
        className={isExpanded && canExpand ? undefined : 'hidden'}
      >
        <div id={contentId} aria-hidden={!isExpanded}>
          {policyQuery.isError && (
            <TextTip color="red" className="mb-4">
              Failed to load this policy. Please try again.
            </TextTip>
          )}
          {status?.Message && (
            <TextTip color={getStatusTipColor(status.Status)} className="mb-4">
              {status.Message}
            </TextTip>
          )}
          {policy && (
            <PolicyReadOnlyContent policy={policy} policyType={policyType} />
          )}
        </div>
      </WidgetBody>
    </Widget>
  );
}

/**
 * Renders the appropriate read-only policy view based on policy type.
 * Each case renders a component that calls only its required hook.
 */
function PolicyReadOnlyContent({
  policy,
  policyType,
}: {
  policy: NonNullable<ReturnType<typeof usePolicy>['data']>;
  policyType: PolicyType;
}) {
  switch (policyType) {
    case 'security-k8s':
      return <SecurityPolicyContent policy={policy} />;
    case 'setup-k8s':
      return <SetupPolicyContent policy={policy} />;
    case 'rbac-k8s':
      return <RBACPolicyContent policy={policy} />;
    case 'registry-k8s':
      return <RegistryPolicyContent policy={policy} />;
    default:
      return null;
  }
}

function SecurityPolicyContent({
  policy,
}: {
  policy: NonNullable<ReturnType<typeof usePolicy>['data']>;
}) {
  const initialValues = useKubernetesSecurityInitialValues(policy);
  return <SecurityPolicyReadOnly formData={initialValues} />;
}

function SetupPolicyContent({
  policy,
}: {
  policy: NonNullable<ReturnType<typeof usePolicy>['data']>;
}) {
  const initialValues = useKubernetesSetupInitialValues(policy);
  return <SetupPolicyReadOnly formData={initialValues} />;
}

function RBACPolicyContent({
  policy,
}: {
  policy: NonNullable<ReturnType<typeof usePolicy>['data']>;
}) {
  const initialValues = useKubernetesRBACInitialValues(policy);
  return <RBACPolicyReadOnly formData={initialValues} />;
}

function RegistryPolicyContent({
  policy,
}: {
  policy: NonNullable<ReturnType<typeof usePolicy>['data']>;
}) {
  const initialValues = useKubernetesRegistryInitialValues(policy);
  return (
    <>
      <RegistryPolicyReadOnly
        formData={{ accessRows: initialValues.accessRows || [] }}
      />
      <AllowedSourcesReadOnly
        restrictSources={initialValues.restrictSources ?? false}
        allowedSources={initialValues.allowedSources || []}
      />
    </>
  );
}
